import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import client, {
  API, formatApiError, fmtTime, fmtDay, dayKey, TYPE_LABEL, fmtHours,
  workedMinutes, hasUnclosedBreak, isoWeekStart, isoWeekId,
} from "../../lib/api";

const AUDIT_LABELS = {
  "auth.login": "Signed in",
  "auth.login.failed": "⚠ Sign-in failed (wrong PIN)",
  "shop.open": "Opened the shop",
  "shop.close": "Closed the shop",
  "auth.setup": "Owner set up",
  "auth.change_pin": "Changed own PIN",
  "owner.create": "Added owner",
  "owner.update": "Updated owner",
  "owner.delete": "Removed owner",
  "employee.create": "Added employee",
  "employee.update": "Updated employee",
  "employee.delete": "Removed employee",
  "employee.reset_pin": "Reset employee PIN",
  "event.delete": "Deleted a punch record",
  "event.edit": "Edited a punch record",
  "event.manual": "Owner punch (on behalf)",
  "event.manual.failed": "⚠ Owner punch rejected (sequence conflict)",
  "event.edit.failed": "⚠ Punch edit rejected (sequence conflict)",
  "event.delete.failed": "⚠ Delete refused (employee on the clock)",
  "events.bulk_delete": "Bulk-deleted records",
  "pay_mark.upsert": "Updated pay mark",
  "admin.export": "Exported data",
  "admin.import": "Imported data (wipe + replace)",
  "punch_request.approve": "✓ Approved missed-punch-out request",
  "punch_request.reject": "✗ Rejected missed-punch-out request",
};

const EVENT_TYPE_LABELS = {
  in: "Punched in",
  out: "Punched out",
  break_start: "Break started",
  break_end: "Break ended",
};

function fmtAuditTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtAuditDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function fmtChanges(changes) {
  if (!changes || typeof changes !== "object") return [];
  const out = [];
  for (const [k, v] of Object.entries(changes)) {
    if (Array.isArray(v) && v.length === 2) {
      const [before, after] = v;
      const b = Array.isArray(before) ? (before.join(", ") || "—") : String(before);
      const a = Array.isArray(after) ? (after.join(", ") || "—") : String(after);
      out.push(`${k}: ${b} → ${a}`);
    } else {
      out.push(`${k}: ${v}`);
    }
  }
  return out;
}

function auditDetailLines(action, meta) {
  if (!meta) return [];
  const lines = [];
  switch (action) {
    case "event.manual": {
      const name = meta.employee_name || "Unknown employee";
      const type = EVENT_TYPE_LABELS[meta.event_type] || meta.event_type || "punch";
      const when = fmtAuditTs(meta.ts);
      lines.push(`${name}${meta.branch ? ` · ${meta.branch}` : ""}`);
      lines.push(`${type}${when ? ` at ${when}` : ""}`);
      break;
    }
    case "event.edit": {
      const name = meta.employee_name || "Unknown employee";
      lines.push(`${name}${meta.branch ? ` · ${meta.branch}` : ""}`);
      const changes = meta.changes || {};
      if (changes.type) {
        const [b, a] = changes.type;
        lines.push(`• type: ${EVENT_TYPE_LABELS[b] || b} → ${EVENT_TYPE_LABELS[a] || a}`);
      }
      if (changes.ts) {
        lines.push(`• time: ${fmtAuditTs(changes.ts[0])} → ${fmtAuditTs(changes.ts[1])}`);
      }
      break;
    }
    case "event.delete": {
      const name = meta.employee_name || meta.name || "Unknown employee";
      const type = EVENT_TYPE_LABELS[meta.event_type] || meta.event_type || "punch";
      const when = fmtAuditTs(meta.ts);
      lines.push(`${name}${meta.branch ? ` · ${meta.branch}` : ""}`);
      lines.push(`${type}${when ? ` at ${when}` : ""}`);
      break;
    }
    case "events.bulk_delete": {
      const total = meta.deleted ?? 0;
      const from = meta.date_from ? fmtAuditDate(meta.date_from) : null;
      const to = meta.date_to ? fmtAuditDate(meta.date_to) : null;
      const rangeBits = [];
      if (from) rangeBits.push(`from ${from}`);
      if (to) rangeBits.push(`to ${to}`);
      const range = rangeBits.length ? rangeBits.join(" ") : "all time";
      lines.push(`${total} record${total === 1 ? "" : "s"} deleted · ${range}`);
      const emps = Array.isArray(meta.employees) ? meta.employees : [];
      if (emps.length > 0) {
        const preview = emps.slice(0, 5).map((e) =>
          `${e.employee_name || "Unknown"}${e.branch ? ` (${e.branch})` : ""}: ${e.count}`
        );
        preview.forEach((p) => lines.push(`• ${p}`));
        if (emps.length > 5) lines.push(`• …and ${emps.length - 5} more`);
      }
      if (meta.branches_scope && meta.branches_scope !== "all") {
        lines.push(`Scope: ${Array.isArray(meta.branches_scope) ? meta.branches_scope.join(", ") : meta.branches_scope}`);
      }
      break;
    }
    case "employee.create":
    case "employee.delete":
    case "employee.reset_pin": {
      const name = meta.employee_name || meta.name || "Unknown employee";
      lines.push(`${name}${meta.branch ? ` · ${meta.branch}` : ""}`);
      break;
    }
    case "employee.update": {
      const name = meta.employee_name || meta.name || "Employee";
      lines.push(`${name}${meta.branch ? ` · ${meta.branch}` : ""}`);
      fmtChanges(meta.changes).forEach((c) => lines.push(`• ${c}`));
      break;
    }
    case "owner.create": {
      lines.push(`${meta.label || "Owner"}${meta.is_super ? " (Super)" : ""}`);
      const bs = Array.isArray(meta.branches) ? meta.branches.join(", ") : "";
      if (bs) lines.push(`Branches: ${bs}`);
      break;
    }
    case "owner.update": {
      if (meta.owner_label) lines.push(meta.owner_label);
      fmtChanges(meta.changes).forEach((c) => lines.push(`• ${c}`));
      break;
    }
    case "owner.delete": {
      lines.push(meta.label || "Owner removed");
      break;
    }
    case "pay_mark.upsert": {
      const name = meta.employee_name || "Employee";
      lines.push(`${name}${meta.branch ? ` · ${meta.branch}` : ""}${meta.week_id ? ` · ${meta.week_id}` : ""}`);
      const bits = [];
      bits.push(meta.paid ? "Marked PAID" : "Marked UNPAID");
      if (meta.note_preview) bits.push(`Note: "${meta.note_preview}"`);
      else if (meta.has_note) bits.push("Note updated");
      lines.push(bits.join(" · "));
      break;
    }
    case "auth.login":
    case "auth.login.failed":
    case "auth.setup":
    case "auth.change_pin":
      break;
    default: {
      const parts = [];
      if (meta.name) parts.push(meta.name);
      if (meta.employee_name) parts.push(meta.employee_name);
      if (meta.label) parts.push(meta.label);
      if (meta.branch) parts.push(meta.branch);
      if (parts.length) lines.push(parts.join(" · "));
    }
  }
  return lines;
}

const FAILURE_ACTIONS = new Set([
  "event.manual.failed",
  "auth.login.failed",
  "event.edit.failed",
  "event.delete.failed",
]);

function AuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [showRejected, setShowRejected] = useState(false);

  useEffect(() => {
    client.get("/owners")
      .then(({ data }) => setOwners(data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = ownerFilter ? `/audit?owner_id=${ownerFilter}` : "/audit";
      const { data } = await client.get(url);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [ownerFilter]);

  useEffect(() => { load(); }, [load]);

  const visibleItems = useMemo(() => {
    if (showRejected) return items;
    return items.filter((a) => !FAILURE_ACTIONS.has(a.action));
  }, [items, showRejected]);

  const rejectedCount = useMemo(
    () => items.filter((a) => FAILURE_ACTIONS.has(a.action)).length,
    [items],
  );

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="bg-panel border border-line rounded-xl p-4.5">
        <div className="caps text-muted-jewel mb-2.5">Filter by owner</div>
        <div className="flex gap-2">
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            data-testid="audit-owner-filter"
            className="flex-1 px-3 py-3 bg-panel-soft text-ivory border border-line rounded-lg text-[15px] focus:outline-none"
          >
            <option value="">All owners</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}{o.is_super ? " (Super)" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={load}
            data-testid="audit-refresh"
            className="seal-outline rounded-lg px-5 text-[14px] font-semibold"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-panel border border-line rounded-xl p-4.5">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="caps text-muted-jewel">
            Activity {loading ? "…" : `(${visibleItems.length}${!showRejected && rejectedCount > 0 ? ` of ${items.length}` : ""})`}
          </div>
          {rejectedCount > 0 && (
            <button
              onClick={() => setShowRejected((v) => !v)}
              data-testid="audit-toggle-rejected"
              className="text-[11px] uppercase tracking-wider text-muted-jewel hover:text-ivory transition-colors border border-line rounded-full px-3 py-1"
            >
              {showRejected ? `Hide rejected (${rejectedCount})` : `Show rejected (${rejectedCount})`}
            </button>
          )}
        </div>
        {visibleItems.length === 0 && !loading && (
          <div className="text-muted-jewel text-[13px]">
            {items.length === 0 ? "No activity yet." : "No successful actions to show."}
          </div>
        )}
        <div className="space-y-2">
          {visibleItems.map((a) => {
            const meta = a.meta || {};
            const label = AUDIT_LABELS[a.action] || a.action;
            const detailLines = auditDetailLines(a.action, meta);
            return (
              <div
                key={a.id}
                className="py-2.5 border-b border-line last:border-b-0"
                data-testid={`audit-row-${a.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] text-ivory">
                      <span className="text-gold font-semibold">{a.owner_label || "—"}</span>
                      <span className="text-muted-jewel"> · </span>
                      {label}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-jewel font-mono whitespace-nowrap shrink-0">
                    {new Date(a.ts).toLocaleString("en-AU", {
                      day: "2-digit", month: "short",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                </div>
                {detailLines.length > 0 && (
                  <div className="mt-1 pl-0.5 text-[12px] text-muted-jewel space-y-0.5">
                    {detailLines.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                )}
                {a.result && a.result !== "ok" && (
                  <div className="mt-1 text-[12px] text-jewel-red">Result: {a.result}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


export default AuditLog;
